/**
 * The one scan-status client, shared by the marketing widget and the
 * dashboard (issue #740).
 *
 * Before this module the poll/SSE logic was forked three ways with three
 * different timeout policies: the dashboard's `startScan` (a 5s grace timer on
 * the first SSE error, no poll fallback at all), the marketing widget (the
 * robust one — bounded SSE errors, a stale timer, a status-poll fallback), and
 * a third poll-only variant inside App.tsx (3s poll, hard 10-minute deadline).
 * This is the marketing widget's implementation promoted, with the poll-only
 * lane folded in as a transport rather than a separate client.
 *
 * ## Timeout policy (one policy, both apps)
 *
 * - At most {@link SCAN_SSE_MAX_CONSECUTIVE_ERRORS} consecutive SSE errors
 *   before the stream is declared lost. Proxy blips are transient; a dead
 *   stream is not. A lost stream is not a failed scan: the client checks
 *   `GET /api/scan-status/:id` at once and continues on the poll transport
 *   (#1688).
 * - Liveness is checked every {@link SCAN_STALE_TIMEOUT_MS} on the SSE
 *   transport and every {@link SCAN_POLL_INTERVAL_MS} on the poll transport,
 *   in both cases by asking `GET /api/scan-status/:id`.
 * - Only *consecutive* dead time counts: any sign of life — an SSE frame, an
 *   open stream, a poll answering `pending` — resets the budget. The scan
 *   fails once {@link SCAN_MAX_STALE_MS} of consecutive dead time accumulates.
 *   Expressing the budget as a duration rather than a cycle count is what lets
 *   the two cadences share it (30s × 20 cycles and 10 minutes are the same
 *   budget the two forks already used).
 *
 * A consequence worth knowing: a scan whose poll keeps answering `pending`
 * never times out here. That was already true of the marketing widget, and the
 * real bound is the backend's own per-scan timeout, which flips the scan to
 * `failed` and reaches us through the poll. The dashboard's old hard 10-minute
 * deadline is gone with the fork.
 */

import { describeScanFailure, ScanFailureCategory } from './scan-failure';
import { mergeProgressFrame, ScanProgress } from './scan-progress';

/** Consecutive `onerror` events tolerated before the stream is declared lost. */
export const SCAN_SSE_MAX_CONSECUTIVE_ERRORS = 5;
/** How long the SSE transport waits for a frame before polling for liveness. */
export const SCAN_STALE_TIMEOUT_MS = 30_000;
/** Poll cadence when there is no stream to wait on. */
export const SCAN_POLL_INTERVAL_MS = 3_000;
/** Consecutive dead time after which the scan is reported as timed out. */
export const SCAN_MAX_STALE_MS = 10 * 60 * 1000;

/** Shape of `GET /api/scan-status/:id`. */
export interface ScanStatusPayload<TResult = unknown> {
  status: 'pending' | 'completed' | 'failed';
  progress?: ScanProgress;
  result?: TResult;
  error?: string | null;
  blockedBySite?: boolean;
}

export interface ScanFailure {
  /**
   * The rest come from `describeScanFailure`. `'connection'` is kept for type
   * compatibility only: since #1688 a lost stream hands off to the status poll,
   * so `watchScan` no longer emits it.
   */
  category: ScanFailureCategory | 'connection';
  /** User-facing copy. Never a bare "Scan failed". */
  message: string;
  /** The raw reason, for analytics — the backend's string where there was one. */
  raw: string;
  /** Analytics label, e.g. `status_poll:bot_blocked`, `sse:timeout`. */
  source: string;
}

/** The slice of `EventSource` this client uses, so tests can supply a double. */
export interface ScanEventSource {
  readyState: number;
  onmessage: ((event: { data: string }) => void) | null;
  onerror: ((event?: unknown) => void) | null;
  close(): void;
}

/**
 * The scan's terminal state, in place of the old `result: TResult | undefined`
 * signal (issue #779 slice 3).
 *
 * `'withheld'` is the case `GET /api/scan-status/:id` withholds a payload for
 * — an **unlocked** scan — not a scan whose `licenseStatus` is `'locked'`.
 * Those two `'locked'`s mean opposite things: `licenseStatus === 'locked'`
 * scans *do* get a (masked) payload on that lane, so they arrive `'delivered'`
 * here. Callers that were doing `if (result) { … } else { /* re-read *\/ }`
 * become `if (terminal.outcome === 'delivered') { … } else { /* re-read *\/ }`.
 */
export type ScanTerminal<TResult = unknown> =
  | { outcome: 'delivered'; scanId: string; result: TResult }
  | { outcome: 'withheld'; scanId: string };

export interface WatchScanOptions<TResult = unknown> {
  /** API root including `/api`, e.g. `https://privisy.onrender.com/api`. */
  apiBase: string;
  scanId: string;
  /**
   * `'sse'` (default) opens the progress stream and polls only for liveness.
   * `'poll'` skips the stream entirely — the dashboard's signed-out lane,
   * which has no stream to attach to.
   */
  transport?: 'sse' | 'poll';
  /** Progress frames, already passed through {@link mergeProgressFrame}. */
  onProgress?: (progress: ScanProgress) => void;
  /** The scan finished — see {@link ScanTerminal} for the two shapes. */
  onComplete?: (terminal: ScanTerminal<TResult>) => void;
  onFailure?: (failure: ScanFailure) => void;
  /** Injectable for tests; defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
  /** Injectable for tests; defaults to `new EventSource(url)`. */
  eventSourceFactory?: (url: string) => ScanEventSource;
}

/** `EventSource.OPEN`, spelled out so this module needs no DOM global. */
const EVENT_SOURCE_OPEN = 1;

/**
 * One failure sends two `'error'` frames, the same two-step as `'complete'`:
 * first the scanner's own progress frame, `{ stage, message, percentage: 100 }`
 * (packages/backend/src/scanner/scanner.ts, `failScan`), then the runner's
 * terminal `{ stage, error, blockedBySite }` (packages/backend/src/api/scan-runner.ts).
 * Only the terminal frame carries the reason and the bot-block flag, so the
 * progress one must neither end the scan nor reach the UI as progress. A frame
 * without `error` is still terminal unless it has the progress shape: the
 * runner drops `error` for a non-`Error` throw. Terminal error frames never
 * carry `percentage`; packages/backend/src/api/scan-progress.test.ts and
 * scan-runner.test.ts pin that shape.
 */
function isErrorProgressFrame(frame: { error?: string; percentage?: number }): boolean {
  return frame.error === undefined && typeof frame.percentage === 'number';
}

/**
 * One read of `GET /api/scan-status/:id`, for the question {@link watchScan}
 * can't answer: does this id exist at all? (watchScan polls through a 404.)
 * `'missing'` is the 404 for an id neither the store nor the queue knows.
 * Throws on any other failure, so a network error never reads as a live scan
 * (#1814).
 */
export async function readScanStatus(options: {
  apiBase: string;
  scanId: string;
  fetchImpl?: typeof fetch;
}): Promise<ScanStatusPayload['status'] | 'missing'> {
  const base = options.apiBase.replace(/\/$/, '');
  const doFetch: typeof fetch = options.fetchImpl ?? ((...args) => fetch(...args));
  const response = await doFetch(`${base}/scan-status/${encodeURIComponent(options.scanId)}`);
  if (response.status === 404) return 'missing';
  if (!response.ok) throw new Error(`scan-status answered ${response.status}`);
  const payload = (await response.json()) as ScanStatusPayload;
  return payload.status;
}

/**
 * Watch a scan to a terminal state.
 *
 * Returns a dispose function: call it on unmount. It is idempotent, and no
 * callback fires after it — including one already scheduled by an in-flight
 * poll.
 */
export function watchScan<TResult = unknown>(options: WatchScanOptions<TResult>): () => void {
  const {
    apiBase,
    scanId,
    transport = 'sse',
    onProgress,
    onComplete,
    onFailure,
    fetchImpl,
    eventSourceFactory,
  } = options;

  const base = apiBase.replace(/\/$/, '');
  const doFetch: typeof fetch = fetchImpl ?? ((...args) => fetch(...args));
  let tickMs = transport === 'sse' ? SCAN_STALE_TIMEOUT_MS : SCAN_POLL_INTERVAL_MS;

  let stopped = false;
  let lastProgress: ScanProgress | null = null;
  let consecutiveErrors = 0;
  let staleMs = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stream: ScanEventSource | null = null;

  const stop = (): void => {
    stopped = true;
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (stream) {
      stream.close();
      stream = null;
    }
  };

  const emitProgress = (next: ScanProgress): void => {
    if (stopped) return;
    lastProgress = mergeProgressFrame(lastProgress, next);
    onProgress?.(lastProgress);
  };

  /** Keep the bar where it is, swap the message — the "still working" nudge. */
  const emitWaitingMessage = (): void => {
    if (!lastProgress) return;
    emitProgress({ ...lastProgress, message: 'Still scanning, please wait...' });
  };

  const finishComplete = (result: TResult | undefined): void => {
    if (stopped) return;
    stop();
    onComplete?.(
      result === undefined ? { outcome: 'withheld', scanId } : { outcome: 'delivered', scanId, result },
    );
  };

  const finishFailure = (
    raw: string | null | undefined,
    blockedBySite: boolean | undefined,
    lane: string,
  ): void => {
    if (stopped) return;
    stop();
    const described = describeScanFailure(raw, blockedBySite);
    onFailure?.({
      category: described.category,
      message: described.message,
      raw: (raw ?? '').trim() || described.message,
      source: `${lane}:${described.category}`,
    });
  };

  const finishStaleTimeout = (): void => {
    if (stopped) return;
    stop();
    onFailure?.({
      category: 'timeout',
      message: describeScanFailure('Scan timed out').message,
      raw: 'Scan timed out',
      source: 'timeout',
    });
  };

  const scheduleTick = (): void => {
    if (stopped) return;
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      void tick();
    }, tickMs);
  };

  /**
   * One liveness check. On the SSE transport this only runs after
   * SCAN_STALE_TIMEOUT_MS of silence; on the poll transport it is the whole
   * mechanism.
   */
  const tick = async (): Promise<void> => {
    if (stopped) return;
    const streamIsHealthy = stream !== null && stream.readyState === EVENT_SOURCE_OPEN;

    try {
      const response = await doFetch(`${base}/scan-status/${encodeURIComponent(scanId)}`);
      if (stopped) return;

      if (response.ok) {
        const payload = (await response.json()) as ScanStatusPayload<TResult>;
        if (stopped) return;

        if (payload.status === 'completed') {
          finishComplete(payload.result);
          return;
        }
        if (payload.status === 'failed') {
          finishFailure(payload.error, payload.blockedBySite, 'status_poll');
          return;
        }
        if (payload.status === 'pending') {
          staleMs = 0;
          // The failure is still `pending` until the report row lands, and in
          // that window the replayed progress is the scanner's error frame.
          if (
            payload.progress &&
            typeof payload.progress.percentage === 'number' &&
            payload.progress.stage !== 'error'
          ) {
            emitProgress(payload.progress);
          } else {
            emitWaitingMessage();
          }
          scheduleTick();
          return;
        }
      }

      // A 404 is not proof of a dead scan: `/api/scan-status/:id` answers
      // `pending` for anything the queue knows about, so a 404 means either a
      // genuinely unknown id or a degraded queue-position lookup. With a
      // healthy stream that is nothing to act on; without one it counts toward
      // the stale budget below like any other silence.
      if (response.status === 404 && streamIsHealthy) {
        staleMs = 0;
        emitWaitingMessage();
        scheduleTick();
        return;
      }
    } catch {
      // Poll failed — indistinguishable from silence, handled below.
    }

    if (stopped) return;

    if (streamIsHealthy) {
      staleMs = 0;
      emitWaitingMessage();
      scheduleTick();
      return;
    }

    staleMs += tickMs;
    if (staleMs < SCAN_MAX_STALE_MS) {
      emitWaitingMessage();
      scheduleTick();
    } else {
      finishStaleTimeout();
    }
  };

  /**
   * The stream is lost, but the scan is not: it keeps running server-side
   * (#1688). Ask /scan-status right away, then keep polling at the poll
   * cadence. tick() settles a terminal status and holds the stale budget, so
   * a finished scan completes instead of surfacing as a connection failure.
   */
  const abandonStream = (): void => {
    if (stopped || !stream) return;
    stream.close();
    stream = null;
    tickMs = SCAN_POLL_INTERVAL_MS;
    staleMs = 0;
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    void tick();
  };

  if (transport === 'sse') {
    const streamUrl = `${base}/scans/stream?scanId=${encodeURIComponent(scanId)}`;
    const factory =
      eventSourceFactory ??
      ((url: string) => new EventSource(url) as unknown as ScanEventSource);
    stream = factory(streamUrl);

    stream.onmessage = (event: { data: string }) => {
      if (stopped) return;
      consecutiveErrors = 0;
      staleMs = 0;
      scheduleTick();

      let frame: ScanProgress & { blockedBySite?: boolean; result?: { blockedBySite?: boolean } };
      try {
        frame = JSON.parse(event.data);
      } catch {
        // A malformed frame is silence, not a failure — the stale budget
        // already covers a stream that only ever sends garbage.
        return;
      }

      if (frame.stage === 'complete' && frame.result) {
        finishComplete(frame.result as unknown as TResult);
      } else if (frame.stage === 'error') {
        if (!isErrorProgressFrame(frame)) {
          // The backend sends `blockedBySite` at the top level. The nested
          // `result.blockedBySite` read is only a fallback for older backends:
          // no current terminal error frame carries `result` (#771).
          finishFailure(frame.error, frame.blockedBySite ?? frame.result?.blockedBySite, 'sse');
        }
      } else if (frame.stage !== 'connected') {
        emitProgress(frame);
      }
    };

    stream.onerror = () => {
      if (stopped) return;
      consecutiveErrors++;
      if (consecutiveErrors >= SCAN_SSE_MAX_CONSECUTIVE_ERRORS) {
        abandonStream();
      }
    };
  }

  if (transport === 'poll') {
    // No stream to wait on: check immediately rather than showing an empty
    // interstitial for a full interval first.
    void tick();
  } else {
    scheduleTick();
  }

  return stop;
}
